import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { followAPI } from '../lib/api';
import { useTranslation } from '../context/LanguageContext';
import { getAvatarFallback } from '../lib/utils';

export function FollowCounts({ userId, followersCount = 0, followingCount = 0 }) {
  return (
    <div className="flex items-center gap-4 text-sm">
      <FollowListDialog userId={userId} type="followers" count={followersCount} />
      <FollowListDialog userId={userId} type="following" count={followingCount} />
    </div>
  );
}

function FollowListDialog({ userId, type, count }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = type === 'followers'
        ? await followAPI.getFollowers(userId)
        : await followAPI.getFollowing(userId);
      setUsers(res.data[type] || []);
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) fetchUsers(); }}>
      <DialogTrigger asChild>
        <button className="hover:text-orange-600 transition-colors">
          <span className="font-bold text-slate-900">{count}</span>{' '}
          <span className="text-slate-600">{type === 'followers' ? t('follow.followers') : t('follow.following')}</span>
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            {type === 'followers' ? t('follow.followers') : t('follow.following')}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-80">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
            </div>
          ) : users.length > 0 ? (
            <div className="space-y-3">
              {users.map((u) => (
                <Link
                  key={u.user_id}
                  to={`/profile/${u.user_id}`}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                  onClick={() => setOpen(false)}
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={u.picture} alt={u.name} />
                    <AvatarFallback className="bg-orange-100 text-orange-700 text-sm">
                      {getAvatarFallback(u.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{u.name}</p>
                    <p className="text-xs text-slate-500 capitalize">{u.trust_level}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">No {type} yet</p>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
